import { createStorage } from "unstorage";
import localStorageDriver from "unstorage/drivers/localstorage";
import memoryDriver from "unstorage/drivers/memory";

/**
 * Android WebViews disable DOM storage by default (window.localStorage is null),
 * and browsers that block site data can throw a SecurityError on access.
 */
const getWebStorage = (): Storage | null => {
  try {
    return window.localStorage ?? null;
  } catch {
    return null;
  }
};

// Without Web Storage, values live in memory for the current page session,
// shared across calls so a later read sees an earlier write. Web Storage needs
// no such caching because the browser already shares it.
let memoryStorage: ReturnType<typeof createStorage> | undefined;

export const useLocalStorage = () => {
  // Client-only: on the server, fail loudly rather than share one memory store
  // across every prerendered page.
  if (!globalThis.window) {
    throw new Error("useLocalStorage() is client-only");
  }
  const webStorage = getWebStorage();
  if (webStorage) {
    return createStorage({
      driver: localStorageDriver({ base: "local", storage: webStorage })
    });
  }
  memoryStorage ??= createStorage({ driver: memoryDriver() });
  return memoryStorage;
};
